import { weekFileName } from '../../lib/weekFileName';
import { ListTotalsBar, sumByCurrency } from '../ui/list-totals-bar';
import { CopyNumberButton } from '../shared/CopyNumberButton';
import useRememberedInputs from '../../hooks/useRememberedInputs';
import { useTranslation } from 'react-i18next';
import { WeekFilter } from '../ui/week-filter';
import { matchesWeek } from '../../lib/weekFilter';
import i18n from '../../i18n';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import RichTextNotes from '../RichTextNotes';
import { Plus, FileText, Download, Eye, Trash2, X, CreditCard, PenLine, RotateCcw, CloudUpload } from 'lucide-react';
import { SortableHeader, useSort, ResetViewButton } from '../ui/SortableHeader';
import { dateLocale } from '../../lib/dateFormat';
import { DateText } from '../ui/week-badge';
import { InvoiceHeaderDisplay, InvoiceHeaderEditor } from './InvoiceHeaderEditor';

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  issued: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800',
  partially_paid: 'bg-yellow-100 text-yellow-800',
};

const fmtCurrency = (amount, currency = 'PLN') => {
  if (amount == null) return '-';
  return new Intl.NumberFormat('pl-PL', { style: 'currency', currency }).format(amount);
};

const fmtDate = (d) => <DateText value={d} empty="-" />;

const EmployeeInvoicesTab = ({ currentUser }) => {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showView, setShowView] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [selected, setSelected] = useState(null);
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager';
  const [sortKey, sortDir, toggleSort, comparator, resetSort] = useSort('issue_date', 'desc', 'employee-invoices');
  const handleSort = (key) => {
    toggleSort(key, key === 'issue_date' || key === 'due_date' ? 'desc' : 'asc');
  };
  // Phase 4 — correction filter
  const [correctionFilter, setCorrectionFilter] = useState('all');
  const [weekFilter, setWeekFilter] = useState(null);
  useRememberedInputs('employee-invoices-filters', {
    correctionFilter: [correctionFilter, setCorrectionFilter], weekFilter: [weekFilter, setWeekFilter],
  });
  const visibleInvoices = invoices.filter((inv) => {
    if (correctionFilter === 'originals' && inv.is_correction) return false;
    if (correctionFilter === 'corrections' && !inv.is_correction) return false;
    if (correctionFilter === 'superseded' && !inv.corrected_by_invoice_id) return false;
    return matchesWeek(weekFilter, inv.issue_date);
  });
  const isViewCustomized = sortKey !== 'issue_date' || sortDir !== 'desc' || correctionFilter !== 'all' || !!weekFilter;
  const handleResetView = () => {
    resetSort();
    setWeekFilter(null);
    setCorrectionFilter('all');
  };

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/api/employee-invoices`);
      setInvoices(r.data);
    } catch { toast.error(t('empInvoiceDlg.failedToLoadEmployeeInvoices')); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleIssue = async (id) => {
    try {
      await axios.post(`${API}/api/employee-invoices/${id}/issue`);
      toast.success(t('empInvoiceDlg.invoiceIssued'));
      fetchInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('empInvoiceDlg.deleteThisInvoiceTheInvoiceNumber'))) return;
    try {
      await axios.delete(`${API}/api/employee-invoices/${id}`);
      toast.success(t('empInvoiceDlg.deleted'));
      fetchInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleRevertToDraft = async (id) => {
    if (!window.confirm(t('empInvoiceDlg.revertThisInvoiceToDraftStatus'))) return;
    try {
      await axios.post(`${API}/api/employee-invoices/${id}/revert-draft`);
      toast.success(t('empInvoiceDlg.invoiceRevertedToDraft'));
      fetchInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleCreateCorrection = async (orig) => {
    const reason = window.prompt(
      `Create a correction (faktura korygująca) for ${orig.invoice_number}?\n\n` +
      `Briefly state the reason for correction (will appear on the FA(3) XML as PrzyczynaKorekty):`,
      'Correction of invoice ' + orig.invoice_number,
    );
    if (reason === null) return;
    try {
      const r = await axios.post(
        `${API}/api/employee-invoices/${orig.id}/create-correction`,
        { correction_reason: reason || 'Korekta faktury', correction_type: 2 },
      );
      toast.success(`Correction draft ${r.data.invoice_number} created — opening for editing…`);
      fetchInvoices();
      setSelected(r.data);
      setShowCreate(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create correction');
    }
  };

  // ---- KSeF upload (Phase 2: Employee invoices) ----
  const [ksefDialogOpen, setKsefDialogOpen] = useState(false);
  const [ksefInvoice, setKsefInvoice] = useState(null);
  const [ksefChecking, setKsefChecking] = useState(false);
  const [ksefStatus, setKsefStatus] = useState(null);
  const [ksefUploading, setKsefUploading] = useState(false);
  const [ksefResult, setKsefResult] = useState(null);

  const openKsefDialog = async (inv) => {
    setKsefInvoice(inv);
    setKsefDialogOpen(true);
    setKsefResult(null);
    setKsefChecking(true);
    try {
      const r = await axios.get(`${API}/api/ksef/employee-invoices/${inv.id}/can-upload`);
      setKsefStatus(r.data);
    } catch (e) {
      setKsefStatus({ can_upload: false, issues: [e.response?.data?.detail || 'Failed to check status'] });
    } finally {
      setKsefChecking(false);
    }
  };

  const handleKsefUpload = async () => {
    if (!ksefInvoice) return;
    setKsefUploading(true);
    setKsefResult(null);
    try {
      const r = await axios.post(`${API}/api/ksef/upload-employee-invoice/${ksefInvoice.id}`);
      if (r.data.success) {
        const ref = r.data.ksef_reference_number || r.data.invoice_reference_number || '';
        toast.success(`Uploaded. Ref: ${ref}`);
        setKsefResult({ ok: true, message: t('empInvoiceDlg.invoiceAcceptedByKsef'), ref });
        fetchInvoices();
      } else {
        const msg = r.data.error || 'Upload failed';
        toast.error(msg, { duration: 6000 });
        setKsefResult({ ok: false, message: msg });
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Upload failed';
      toast.error(msg, { duration: 6000 });
      setKsefResult({ ok: false, message: msg });
    } finally {
      setKsefUploading(false);
    }
  };

  const closeKsefDialog = (open) => {
    if (ksefUploading) return;
    setKsefDialogOpen(open);
    if (!open) {
      setKsefInvoice(null);
      setKsefResult(null);
      setKsefStatus(null);
    }
  };

  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const [pdfInvoice, setPdfInvoice] = useState(null);
  const [includeTimesheets, setIncludeTimesheets] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async (inv) => {
    // If called from dialog with timesheets option
    setPdfInvoice(inv);
    setShowPdfDialog(true);
  };

  const doDownload = async () => {
    if (!pdfInvoice) return;
    setDownloading(true);
    try {
      const url = `${API}/api/employee-invoices/${pdfInvoice.id}/pdf${includeTimesheets ? '?include_timesheets=true' : ''}`;
      const r = await axios.get(url, { responseType: 'blob' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(new Blob([r.data]));
      link.setAttribute('download', weekFileName(`employee_invoice_${(pdfInvoice.invoice_number || '').replace(/\//g, '_')}.pdf`));
      document.body.appendChild(link);
      link.click();
      link.remove();
      setShowPdfDialog(false);
      setIncludeTimesheets(false);
      setPdfInvoice(null);
    } catch { toast.error(t('empInvoiceDlg.failedToDownloadPdf')); }
    setDownloading(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-800" data-testid="employee-invoices-heading">
            {isAdmin ? t('empInvoiceDlg.employeeInvoices') : t('empInvoiceDlg.myInvoices')}
          </h2>
          <p className="text-slate-500">
            {isAdmin ? t('empInvoiceDlg.manageInvoicesFromEmployees') : t('empInvoiceDlg.createAndManageYourInvoicesTo')}
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="new-emp-invoice-btn">
          <Plus className="h-4 w-4 mr-2" /> {t('invoices.newInvoice')}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('empInvoiceDlg.invoices')}</CardTitle>
              <CardDescription>{t('empInvoiceDlg.invoiceCount', { count: visibleInvoices.length })}</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={correctionFilter} onValueChange={setCorrectionFilter}>
                <SelectTrigger className="w-[140px]" data-testid="emp-correction-filter">
                  <SelectValue placeholder={t('empInvoiceDlg.type')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('empInvoiceDlg.allInvoices')}</SelectItem>
                  <SelectItem value="originals">{t('empInvoiceDlg.originals')}</SelectItem>
                  <SelectItem value="corrections">{t('empInvoiceDlg.corrections')}</SelectItem>
                  <SelectItem value="superseded">{t('empInvoiceDlg.superseded')}</SelectItem>
                </SelectContent>
              </Select>
              <WeekFilter value={weekFilter} onChange={setWeekFilter} testId="emp-invoice-week-filter" />
              <ResetViewButton show={isViewCustomized} onReset={handleResetView} testId="reset-view-employee-invoices" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ListTotalsBar
            count={visibleInvoices.length}
            week={weekFilter}
            groups={[
              { key: 'net', label: t('totals.net'), values: sumByCurrency(visibleInvoices, (r) => r.currency, (r) => r.total_net) },
              { key: 'gross', label: t('totals.gross'), values: sumByCurrency(visibleInvoices, (r) => r.currency, (r) => r.total_gross) },
            ]}
            testId="emp-invoice-totals"
            copyable
            className="-mx-6 -mt-2 mb-3"
          />
          {loading ? (
            <div className="text-center py-8 text-slate-500">{t('common.loading')}</div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-8 text-slate-500" data-testid="no-emp-invoices">{t('empInvoiceDlg.noInvoicesYet')}</div>
          ) : (
            <Table data-testid="emp-invoices-table">
              <TableHeader>
                <TableRow>
                  <SortableHeader column="invoice_number" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>{t('auto.misc2.invoice')}</SortableHeader>
                  {isAdmin && <SortableHeader column="employee" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>{t('empInvoiceDlg.employee')}</SortableHeader>}
                  <SortableHeader column="issue_date" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>{t('empInvoiceDlg.issueDate')}</SortableHeader>
                  <SortableHeader column="due_date" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>{t('empInvoiceDlg.dueDate')}</SortableHeader>
                  <SortableHeader column="amount" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right">{t('empInvoiceDlg.gross')}</SortableHeader>
                  <SortableHeader column="status" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>{t('common.status')}</SortableHeader>
                  <TableHead className="text-right">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...visibleInvoices].sort(comparator((inv, key) => {
                  switch (key) {
                    case 'invoice_number': return inv.invoice_number || '';
                    case 'employee': return (inv.employee_name || '').toLowerCase();
                    case 'issue_date': return inv.issue_date ? new Date(inv.issue_date).getTime() : 0;
                    case 'due_date': return inv.due_date ? new Date(inv.due_date).getTime() : 0;
                    case 'amount': return inv.total_gross ?? 0;
                    case 'status': return inv.status || '';
                    default: return '';
                  }
                })).map(inv => {
                  const corrBy = inv.corrected_by_invoice_id
                    ? invoices.find(x => x.id === inv.corrected_by_invoice_id)
                    : null;
                  return (
                  <TableRow key={inv.id}>
                    <TableCell className="font-medium">
                      <div>{inv.invoice_number}</div>
                      {inv.is_correction && inv.corrects_invoice_number && (
                        <div className="text-[10px] text-blue-700 leading-tight" title={t('empInvoiceDlg.thisIsACorrectionFakturaKoryguj')}>
                          ← Corrects <span className="font-mono">{inv.corrects_invoice_number}</span>
                        </div>
                      )}
                      {corrBy && (
                        <div className="text-[10px] text-amber-700 leading-tight" title={t('empInvoiceDlg.supersededByCorrection')}>
                          → Corrected by <span className="font-mono">{corrBy.invoice_number}</span>
                        </div>
                      )}
                    </TableCell>
                    {isAdmin && <TableCell>{inv.employee_name || '-'}</TableCell>}
                    <TableCell>{fmtDate(inv.issue_date)}</TableCell>
                    <TableCell>{fmtDate(inv.due_date)}</TableCell>
                    <TableCell className="text-right">
                      <span className="inline-flex items-center justify-end gap-0.5 whitespace-nowrap">
                        {fmtCurrency(inv.total_gross, inv.currency)}
                        <CopyNumberButton value={inv.total_gross} size="sm" testId={`copy-emp-invoice-gross-${inv.id}`} />
                      </span>
                    </TableCell>
                    <TableCell><Badge className={statusColors[inv.status]}>{t(`invoices.status.${inv.status}`, { defaultValue: inv.status })}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" title={t('empInvoiceDlg.view')} onClick={() => { setSelected(inv); setShowView(true); }} data-testid={`emp-view-${inv.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title={t('empInvoiceDlg.pdf')} onClick={() => handleDownload(inv)} data-testid={`emp-download-${inv.id}`}>
                          <Download className="h-4 w-4" />
                        </Button>
                        {inv.status === 'draft' && (
                          <Button variant="ghost" size="icon" title={t('empInvoiceDlg.issue')} onClick={() => handleIssue(inv.id)}>
                            <FileText className="h-4 w-4 text-blue-600" />
                          </Button>
                        )}
                        {inv.status !== 'draft' && isAdmin && (
                          <Button variant="ghost" size="icon" title={t('empInvoiceDlg.revertToDraft')} onClick={() => handleRevertToDraft(inv.id)}>
                            <RotateCcw className="h-4 w-4 text-orange-600" />
                          </Button>
                        )}
                        {inv.status !== 'draft' && (
                          <Button
                            variant="ghost" size="icon"
                            title={inv.ksef_reference_number ? `KSeF: ${inv.ksef_reference_number}` : t('empInvoiceDlg.uploadToKsef')}
                            onClick={() => openKsefDialog(inv)}
                            data-testid="emp-ksef-upload-btn"
                          >
                            <CloudUpload className={`h-4 w-4 ${inv.ksef_reference_number ? 'text-emerald-600' : 'text-slate-500'}`} />
                          </Button>
                        )}
                        {['issued', 'paid', 'partially_paid'].includes(inv.status)
                          && !inv.is_correction && !inv.corrected_by_invoice_id && (
                          <Button variant="ghost" size="icon" title={t('empInvoiceDlg.createCorrectionFakturaKorygujCa')}
                            onClick={() => handleCreateCorrection(inv)}
                            data-testid={`emp-correct-${inv.id}`}>
                            <RotateCcw className="h-4 w-4 text-blue-600" />
                          </Button>
                        )}
                        {inv.is_correction && (
                          <span className="inline-flex items-center px-1.5 h-6 rounded text-[10px] font-medium bg-blue-100 text-blue-800"
                            title={`Correction of ${inv.corrects_invoice_number || ''}`}>
                            {t('auto.misc2.kor')}
                          </span>
                        )}
                        <Button variant="ghost" size="icon" title={t('common.edit')} onClick={() => { setSelected(inv); setShowCreate(true); }} data-testid={`emp-edit-${inv.id}`}>
                          <PenLine className="h-4 w-4 text-amber-600" />
                        </Button>
                        <Button variant="ghost" size="icon" title={t('common.delete')} onClick={() => handleDelete(inv.id)}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                        {isAdmin && ['issued', 'partially_paid'].includes(inv.status) && (
                          <Button variant="ghost" size="icon" title={t('empInvoiceDlg.payment')} onClick={() => { setSelected(inv); setShowPayment(true); }}>
                            <CreditCard className="h-4 w-4 text-green-600" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CreateEmployeeInvoiceDialog
        open={showCreate}
        onOpenChange={(v) => { setShowCreate(v); if (!v) setSelected(null); }}
        editInvoice={selected || null}
        currentUser={currentUser}
        onSuccess={() => { fetchInvoices(); setSelected(null); }}
      />

      {/* KSeF Upload Dialog */}
      <Dialog open={ksefDialogOpen} onOpenChange={closeKsefDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="emp-ksef-upload-dialog">
          <DialogHeader>
            <DialogTitle>{t('empInvoiceDlg.uploadToKsef')}</DialogTitle>
            <DialogDescription>
              {ksefInvoice ? (<>{t('empInvoiceDlg.submitInvoice')}<span className="font-mono">{ksefInvoice.invoice_number}</span>{t('empInvoiceDlg.toTheNationalEInvoicingSystem')}</>) : t('empInvoiceDlg.submitToKsef')}
            </DialogDescription>
          </DialogHeader>

          {ksefChecking && (
            <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-700 flex items-center gap-2">
              <span className="inline-block h-3 w-3 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
              {t('empInvoiceDlg.checkingEligibility')}
            </div>
          )}

          {!ksefChecking && ksefStatus && !ksefResult && (
            <>
              {ksefStatus.already_uploaded ? (
                <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-900">
                  <div className="font-semibold">{t('empInvoiceDlg.alreadyUploadedToKsef')}</div>
                  <div className="mt-1 break-all">
                    Reference: <span className="font-mono text-xs">{ksefStatus.ksef_reference_number || '(pending)'}</span>
                  </div>
                </div>
              ) : ksefStatus.can_upload ? (
                <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-900">
                  ✓ Ready to upload. The invoice will be sent to KSeF using the seller and buyer details exactly as they appear on this invoice. Open the invoice and edit the Seller / Buyer panels first if anything is incorrect.
                </div>
              ) : (
                <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-900">
                  <div className="font-semibold mb-1">{t('empInvoiceDlg.cannotUploadYet')}</div>
                  <ul className="list-disc list-inside space-y-0.5 text-xs">
                    {(ksefStatus.issues || []).map((iss, idx) => (
                      <li key={idx}>{iss}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {ksefUploading && (
            <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-700 flex items-center gap-2">
              <span className="inline-block h-3 w-3 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
              {t('empInvoiceDlg.uploadingToKsefThisCanTake')}
            </div>
          )}

          {ksefResult?.ok && (
            <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-800">
              <div className="font-semibold">{t('empInvoiceDlg.submittedToKsef')}</div>
              {ksefResult.ref && (
                <div className="mt-1 break-all">
                  Reference: <span className="font-mono text-xs">{ksefResult.ref}</span>
                </div>
              )}
            </div>
          )}

          {ksefResult && !ksefResult.ok && (
            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800 max-w-full overflow-hidden">
              <div className="font-semibold">{t('empInvoiceDlg.ksefRejectedTheUpload')}</div>
              <div className="mt-2 max-h-40 overflow-auto break-all whitespace-pre-wrap font-mono text-xs leading-relaxed">
                {ksefResult.message}
              </div>
            </div>
          )}

          <DialogFooter>
            {ksefResult?.ok || ksefStatus?.already_uploaded ? (
              <Button onClick={() => closeKsefDialog(false)} data-testid="emp-ksef-done-btn">{t('empInvoiceDlg.done')}</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => closeKsefDialog(false)} disabled={ksefUploading} data-testid="dialog-cancel-btn">{t('common.cancel')}</Button>
                <Button
                  onClick={handleKsefUpload}
                  disabled={ksefUploading || ksefChecking || !ksefStatus?.can_upload}
                  data-testid="emp-ksef-confirm-btn"
                >
                  {ksefUploading ? t('empInvoiceDlg.uploading') : (ksefResult ? t('empInvoiceDlg.retry') : t('empInvoiceDlg.uploadToKsef'))}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showView && selected && (
        <Dialog open={showView} onOpenChange={setShowView}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="view-emp-invoice-dialog">
            <DialogHeader>
              <DialogTitle>Invoice {selected.invoice_number}</DialogTitle>
              <DialogDescription>{t('empInvoiceDlg.employeeInvoiceDetails')}</DialogDescription>
            </DialogHeader>
            <Badge className={statusColors[selected.status]}>{t(`invoices.status.${selected.status}`, { defaultValue: selected.status })}</Badge>
            <InvoiceHeaderDisplay invoice={selected} />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
              <div><div className="text-sm text-slate-500">{t('empInvoiceDlg.employee')}</div><div className="font-medium">{selected.employee_name}</div></div>
              <div><div className="text-sm text-slate-500">{t('empInvoiceDlg.issueDate')}</div><div>{fmtDate(selected.issue_date)}</div></div>
              <div><div className="text-sm text-slate-500">{t('empInvoiceDlg.dueDate')}</div><div>{fmtDate(selected.due_date)}</div></div>
            </div>
            <Table>
              <TableHeader><TableRow>
                <TableHead>#</TableHead><TableHead>{t('empInvoiceDlg.description')}</TableHead><TableHead className="text-right">{t('invoiceDlg.qty')}</TableHead>
                <TableHead>{t('empInvoiceDlg.unit')}</TableHead><TableHead className="text-right">{t('empInvoiceDlg.price')}</TableHead>
                <TableHead className="text-right">{t('empInvoiceDlg.net')}</TableHead><TableHead className="text-right">{t('empInvoiceDlg.gross')}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(selected.line_items || []).map((it, i) => (
                  <TableRow key={it.id || i}>
                    <TableCell>{i + 1}</TableCell><TableCell>{it.description}</TableCell>
                    <TableCell className="text-right">{it.quantity}</TableCell><TableCell>{it.unit}</TableCell>
                    <TableCell className="text-right">{fmtCurrency(it.unit_price, selected.currency)}</TableCell>
                    <TableCell className="text-right">{fmtCurrency(it.net_amount, selected.currency)}</TableCell>
                    <TableCell className="text-right">{fmtCurrency(it.gross_amount, selected.currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="text-right space-y-1 border-t pt-3">
              <div>{t('empInvoiceDlg.net2')}<strong>{fmtCurrency(selected.total_net, selected.currency)}</strong></div>
              <div>VAT ({selected.vat_rate}%): <strong>{fmtCurrency(selected.total_vat, selected.currency)}</strong></div>
              <div className="text-lg text-blue-600">{t('empInvoiceDlg.gross2')}<strong>{fmtCurrency(selected.total_gross, selected.currency)}</strong></div>
              <div className="text-sm">{t('invoiceDlg.paidLabel')} {fmtCurrency(selected.amount_paid, selected.currency)} | Remaining: {fmtCurrency(selected.amount_remaining, selected.currency)}</div>
            </div>
            {selected.bank_account && <div className="text-sm mt-2"><strong>{t('empInvoiceDlg.bank')}</strong> {selected.bank_account}</div>}
            {selected.notes && <div className="text-sm"><strong>{t('empInvoiceDlg.notes')}</strong> <div dangerouslySetInnerHTML={{ __html: selected.notes }} /></div>}
            <DialogFooter><Button variant="outline" onClick={() => setShowView(false)} data-testid="dialog-close-btn">{t('common.close')}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {showPayment && selected && (
        <PaymentDialog
          open={showPayment}
          onOpenChange={setShowPayment}
          invoice={selected}
          onSuccess={fetchInvoices}
          endpoint="employee-invoices"
        />
      )}

      {showPdfDialog && pdfInvoice && (
        <Dialog open={showPdfDialog} onOpenChange={(v) => { setShowPdfDialog(v); if (!v) { setPdfInvoice(null); setIncludeTimesheets(false); } }}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>{t('empInvoiceDlg.downloadPdf')}</DialogTitle></DialogHeader>
            <div className="py-4 space-y-3">
              <p className="text-sm text-slate-600">Invoice: {pdfInvoice.invoice_number}</p>
              <div className="flex items-center gap-2">
                <Checkbox id="emp-ts-check" checked={includeTimesheets} onCheckedChange={setIncludeTimesheets} data-testid="emp-include-timesheets" />
                <Label htmlFor="emp-ts-check">{t('empInvoiceDlg.includeAssociatedTimesheets')}</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPdfDialog(false)} data-testid="dialog-cancel-btn">{t('common.cancel')}</Button>
              <Button onClick={doDownload} disabled={downloading} data-testid="emp-download-pdf-btn">
                {downloading ? t('empInvoiceDlg.generating') : t('empInvoiceDlg.downloadPdf')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// ==================== Create Employee Invoice ====================
const CreateEmployeeInvoiceDialog = ({ open, onOpenChange, editInvoice, currentUser, onSuccess }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState('timesheet');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [tsData, setTsData] = useState(null);
  const [loadingTs, setLoadingTs] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    issue_date: '', sale_date: '', due_date: '', currency: 'PLN', vat_rate: 23,
    notes: '', bank_account: '', seller_name: '', seller_address: '', seller_tax_id: '',
    _bank_pln: '', _bank_eur: '', line_items: [],
    reverse_charge: true,
    reverse_charge_text: '„ODWROTNE OBCIĄŻENIE" PODATEK VAT OPŁACA NABYWCA USŁUGI, Opodatkowanie w kraju położenia nieruchomości (art. 28e ustawy o VAT i art. 47 dyrektywy 2006/112/WE);\n"REVERSE CHARGE" VAT IS PAID BY THE BUYER OF THE SERVICE, Taxation in the country where the real estate is located (Article 28e of the VAT Act and Article 47 of Directive 2006/112/EC)',
    // Phase 2 — buyer can be any contractor
    buyer_name: '', buyer_address: '', buyer_tax_id: '', buyer_vat_number: '',
    buyer_country_code: '', saved_buyer_id: null,
    header_enabled: false, header_lines: [], header_logo_id: null, header_logo_size: 'medium', header_logo_position: 'left', header_accent_color: '#AA0000',
  });

  const [savedItems, setSavedItems] = useState([]);
  const [showSavedItemsPicker, setShowSavedItemsPicker] = useState(false);
  const [savedBuyers, setSavedBuyers] = useState([]);
  const [showSavedBuyersPicker, setShowSavedBuyersPicker] = useState(false);
  const fetchSavedItems = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/employee-saved-invoice-items`);
      setSavedItems(r.data || []);
    } catch { /* private endpoint, may 403 for some — ignore */ }
  }, []);
  const fetchSavedBuyers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/employee-saved-buyers`);
      setSavedBuyers(r.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!open) return;
    fetchSavedItems();
    fetchSavedBuyers();
    const now = new Date();
    const y = now.getFullYear(), m = now.getMonth();
    const firstDay = format(new Date(y, m, 1), 'yyyy-MM-dd');
    const lastDay = format(new Date(y, m + 1, 0), 'yyyy-MM-dd');
    const dueDate = format(new Date(now.getTime() + 14 * 86400000), 'yyyy-MM-dd');

    if (editInvoice) {
      setStep(2);
      setForm({
        issue_date: (editInvoice.issue_date || '').substring(0, 10),
        sale_date: (editInvoice.sale_date || '').substring(0, 10),
        due_date: (editInvoice.due_date || '').substring(0, 10),
        currency: editInvoice.currency || 'PLN',
        vat_rate: editInvoice.vat_rate ?? 23,
        notes: editInvoice.notes || '',
        bank_account: editInvoice.bank_account || '',
        seller_name: editInvoice.seller_name || '',
        seller_address: editInvoice.seller_address || '',
        seller_tax_id: editInvoice.seller_tax_id || '',
        line_items: editInvoice.line_items || [],
        reverse_charge: editInvoice.reverse_charge ?? true,
        reverse_charge_text: editInvoice.reverse_charge_text || '„ODWROTNE OBCIĄŻENIE" PODATEK VAT OPŁACA NABYWCA USŁUGI, Opodatkowanie w kraju położenia nieruchomości (art. 28e ustawy o VAT i art. 47 dyrektywy 2006/112/WE);\n"REVERSE CHARGE" VAT IS PAID BY THE BUYER OF THE SERVICE, Taxation in the country where the real estate is located (Article 28e of the VAT Act and Article 47 of Directive 2006/112/EC)',
        issued_by_name: editInvoice.issued_by_name || '',
        invoice_number: editInvoice.invoice_number || '',
        // Phase 2 — buyer
        buyer_name: editInvoice.buyer_name || '',
        buyer_address: editInvoice.buyer_address || '',
        buyer_tax_id: editInvoice.buyer_tax_id || '',
        buyer_vat_number: editInvoice.buyer_vat_number || '',
        buyer_country_code: editInvoice.buyer_country_code || '',
        saved_buyer_id: editInvoice.saved_buyer_id || null,
        header_enabled: !!editInvoice.header_enabled,
        header_lines: editInvoice.header_lines || [],
        header_logo_id: editInvoice.header_logo_id || null,
        header_logo_size: editInvoice.header_logo_size || 'medium',
        header_logo_position: editInvoice.header_logo_position || 'left',
        header_accent_color: editInvoice.header_accent_color || '#AA0000',
      });
      setStep(2);
    } else {
      setStep(1);
      setMode('timesheet');
      setPeriodStart(firstDay);
      setPeriodEnd(lastDay);
      setTsData(null);
      setForm({
        issue_date: lastDay, sale_date: lastDay, due_date: dueDate,
        currency: 'PLN', vat_rate: 0, notes: '', bank_account: '',
        seller_name: '', seller_address: '', seller_tax_id: '',
        _bank_pln: '', _bank_eur: '', _bank_gbp: '', _bank_usd: '', line_items: [],
        reverse_charge: true,
        reverse_charge_text: '„ODWROTNE OBCIĄŻENIE" PODATEK VAT OPŁACA NABYWCA USŁUGI, Opodatkowanie w kraju położenia nieruchomości (art. 28e ustawy o VAT i art. 47 dyrektywy 2006/112/WE);\n"REVERSE CHARGE" VAT IS PAID BY THE BUYER OF THE SERVICE, Taxation in the country where the real estate is located (Article 28e of the VAT Act and Article 47 of Directive 2006/112/EC)',
        issued_by_name: currentUser?.full_name || currentUser?.username || '',
        invoice_number: '',
        // Phase 2 — buyer
        buyer_name: '', buyer_address: '', buyer_tax_id: '', buyer_vat_number: '', buyer_country_code: '',
        saved_buyer_id: null,
        header_enabled: false, header_lines: [], header_logo_id: null, header_logo_size: 'medium', header_logo_position: 'left', header_accent_color: '#AA0000',
      });
      // Fetch employee profile to pre-fill bank account and seller info
      axios.get(`${API}/api/employee-invoices/my-profile`).then(r => {
        setForm(prev => {
          const bankForCurrency = {
            'PLN': r.data.bank_account_pln,
            'EUR': r.data.bank_account_eur,
            'GBP': r.data.bank_account_gbp,
            'USD': r.data.bank_account_usd,
          }[prev.currency] || '';
          return {
            ...prev,
            bank_account: bankForCurrency || '',
            seller_name: r.data.business_name || r.data.name || prev.seller_name,
            seller_address: r.data.address || prev.seller_address,
            seller_tax_id: r.data.tax_id || prev.seller_tax_id,
            _bank_pln: r.data.bank_account_pln || '',
            _bank_eur: r.data.bank_account_eur || '',
            _bank_gbp: r.data.bank_account_gbp || '',
            _bank_usd: r.data.bank_account_usd || '',
          };
        });
      }).catch(() => {});
    }
  }, [open, editInvoice, fetchSavedItems, fetchSavedBuyers]);

  // Keep `bank_account` in sync with the currency selection. This covers
  // the timesheet-mode flow where the project currency overwrites
  // form.currency AFTER my-profile has loaded — without this the bank
  // field would remain on the old PLN account silently.
  useEffect(() => {
    if (editInvoice) return;  // edit mode: keep whatever's on the invoice
    setForm(prev => {
      const map = { PLN: prev._bank_pln, EUR: prev._bank_eur, GBP: prev._bank_gbp, USD: prev._bank_usd };
      const fresh = map[prev.currency] || '';
      // Only auto-fill if the user hasn't manually typed a different
      // value: detect "manually set" by checking whether bank_account
      // is one of the known cached IBANs.
      const cachedSet = new Set([prev._bank_pln, prev._bank_eur, prev._bank_gbp, prev._bank_usd].filter(Boolean));
      if (prev.bank_account && !cachedSet.has(prev.bank_account)) return prev;
      return { ...prev, bank_account: fresh };
    });
  }, [form.currency, form._bank_pln, form._bank_eur, form._bank_gbp, form._bank_usd, editInvoice]);

  // ---- Saved items (Phase 2) ----
  const addItemFromSaved = (saved) => {
    setForm(prev => ({
      ...prev,
      line_items: [...prev.line_items, {
        id: crypto.randomUUID(),
        description: saved.description || saved.name,
        quantity: saved.default_quantity || 1,
        unit: saved.unit || 'hour',
        unit_price: saved.default_unit_price || 0,
        net_amount: (saved.default_quantity || 1) * (saved.default_unit_price || 0),
        source_type: 'saved',
        source_id: saved.id,
      }],
    }));
    setShowSavedItemsPicker(false);
  };

  const handleSaveItemForReuse = async (item) => {
    const itemName = window.prompt('Save this item for reuse — give it a short name:', (item.description || '').slice(0, 60));
    if (!itemName) return;
    try {
      await axios.post(`${API}/api/employee-saved-invoice-items`, {
        name: itemName,
        description: item.description,
        unit: item.unit || 'hour',
        default_quantity: item.quantity || 1,
        default_unit_price: item.unit_price || null,
        default_currency: form.currency || 'PLN',
      });
      toast.success(t('empInvoiceDlg.savedForReuse'));
      fetchSavedItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDeleteSavedItem = async (id) => {
    if (!window.confirm(t('empInvoiceDlg.deleteThisSavedItem'))) return;
    try {
      await axios.delete(`${API}/api/employee-saved-invoice-items/${id}`);
      fetchSavedItems();
    } catch { toast.error(t('empInvoiceDlg.failedToDelete')); }
  };

  // ---- Saved buyers (Phase 3 — feature 3) ----
  const applySavedBuyer = (b) => {
    setForm(prev => ({
      ...prev,
      buyer_name: b.name || '',
      buyer_address: b.address || '',
      buyer_tax_id: b.tax_id || '',
      buyer_vat_number: b.vat_number || '',
      buyer_country_code: b.country_code || 'PL',
      saved_buyer_id: b.id,
    }));
    setShowSavedBuyersPicker(false);
    toast.success(`Buyer set: ${b.name}`);
  };

  const handleSaveCurrentBuyer = async () => {
    if (!form.buyer_name?.trim()) {
      toast.error(t('empInvoiceDlg.fillInTheBuyerNameFirst'));
      return;
    }
    try {
      await axios.post(`${API}/api/employee-saved-buyers`, {
        name: form.buyer_name.trim(),
        address: form.buyer_address || '',
        tax_id: form.buyer_tax_id || '',
        vat_number: form.buyer_vat_number || '',
        country_code: form.buyer_country_code || 'PL',
      });
      toast.success(t('empInvoiceDlg.savedForReuse'));
      fetchSavedBuyers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDeleteSavedBuyer = async (id) => {
    if (!window.confirm(t('empInvoiceDlg.deleteThisSavedBuyer'))) return;
    try {
      await axios.delete(`${API}/api/employee-saved-buyers/${id}`);
      fetchSavedBuyers();
    } catch { toast.error(t('empInvoiceDlg.failedToDelete')); }
  };

  const loadTimesheets = async () => {
    if (!periodStart || !periodEnd) { toast.error(t('empInvoiceDlg.selectPeriod')); return; }
    setLoadingTs(true);
    try {
      const r = await axios.get(`${API}/api/employee-invoices/my-timesheets?start_date=${periodStart}&end_date=${periodEnd}`);
      setTsData(r.data);
      // Backend may return multiple rows per project when the hourly
      // rate changed mid-period — each row carries its own period and
      // rate. We render them as separate invoice line items so the
      // payee sees exactly which rate was applied to which slice of
      // their work.
      const buildDescription = (p) => {
        const base = `Electrical and mechanical installation - ${p.project_name}`;
        if (p.rate_segments && p.rate_segments > 1 && p.period_start && p.period_end) {
          return `${base} (${p.period_start} to ${p.period_end} @ ${Number(p.hourly_rate || 0).toFixed(2)} ${p.currency}/h)`;
        }
        return `${base} (${periodStart} to ${periodEnd})`;
      };
      const items = (r.data.projects || []).filter(p => p.total_hours > 0).map(p => ({
        id: crypto.randomUUID(),
        description: buildDescription(p),
        quantity: p.total_hours,
        unit: 'hour',
        unit_price: p.hourly_rate || 0,
        net_amount: p.total_hours * (p.hourly_rate || 0),
        source_type: 'timesheet',
      }));
      if (items.length === 0 && r.data.projects?.length > 0) {
        // Even if hours are 0, create template line items
        const templateItems = r.data.projects.map(p => ({
          id: crypto.randomUUID(),
          description: buildDescription(p),
          quantity: p.total_hours || 0,
          unit: 'hour',
          unit_price: p.hourly_rate || 0,
          net_amount: (p.total_hours || 0) * (p.hourly_rate || 0),
          source_type: 'timesheet',
        }));
        setForm(prev => ({ ...prev, line_items: templateItems, currency: r.data.projects[0]?.currency || prev.currency }));
      } else {
        setForm(prev => ({ ...prev, line_items: items, currency: items.length > 0 ? (r.data.projects[0]?.currency || prev.currency) : prev.currency }));
      }
      setStep(2);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load timesheets'); }
    finally { setLoadingTs(false); }
  };

  const addItem = () => {
    setForm(prev => ({ ...prev, line_items: [...prev.line_items, {
      id: crypto.randomUUID(), description: '', quantity: 1, unit: 'hour', unit_price: 0, net_amount: 0, source_type: 'manual',
    }] }));
  };

  const removeItem = (i) => setForm(prev => ({ ...prev, line_items: prev.line_items.filter((_, idx) => idx !== i) }));

  const updateItem = (i, field, value) => {
    setForm(prev => {
      const items = [...prev.line_items];
      items[i] = { ...items[i], [field]: value };
      items[i].net_amount = (items[i].quantity || 0) * (items[i].unit_price || 0);
      return { ...prev, line_items: items };
    });
  };

  const totalNet = form.line_items.reduce((s, it) => s + (it.net_amount || 0), 0);
  const totalVat = form.reverse_charge ? 0 : totalNet * form.vat_rate / 100;
  const totalGross = totalNet + totalVat;

  const handleSave = async () => {
    if (!form.line_items.length) { toast.error(t('empInvoiceDlg.addAtLeastOneItem')); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        issue_date: new Date(form.issue_date).toISOString(),
        sale_date: new Date(form.sale_date).toISOString(),
        due_date: new Date(form.due_date).toISOString(),
      };
      if (editInvoice) {
        await axios.put(`${API}/api/employee-invoices/${editInvoice.id}`, payload);
        toast.success(t('empInvoiceDlg.invoiceUpdated'));
      } else {
        await axios.post(`${API}/api/employee-invoices`, payload);
        toast.success(t('empInvoiceDlg.invoiceCreated'));
      }
      onOpenChange(false);
      onSuccess();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="create-emp-invoice-dialog">
        <DialogHeader>
          <DialogTitle>
            {editInvoice
              ? (editInvoice.is_correction ? `Edit correction (KOR) — ${editInvoice.invoice_number || ''}` : t('empInvoiceDlg.editInvoice'))
              : t('empInvoiceDlg.createEmployeeInvoice')}
          </DialogTitle>
          {editInvoice?.is_correction && (
            <DialogDescription className="text-blue-800 bg-blue-50 px-2 py-1.5 rounded mt-2 text-xs">
              <strong>{t('empInvoiceDlg.fakturaKorygujCa')}</strong> — corrects invoice
              <span className="font-mono mx-1">{editInvoice.corrects_invoice_number}</span>
              {editInvoice.corrects_ksef_number && (
                <>{t('auto.misc2.ksefRef')} <span className="font-mono">{editInvoice.corrects_ksef_number}</span>)</>
              )}
              . Edit the line items and amounts to reflect the corrected values, then save and upload to KSeF.
            </DialogDescription>
          )}
          <DialogDescription>{step === 1 ? t('empInvoiceDlg.chooseHowToCreate') : t('empInvoiceDlg.reviewAndCustomize')}</DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button className={`p-4 border-2 rounded-lg text-left transition-colors ${mode === 'timesheet' ? 'border-blue-600 bg-blue-50' : 'border-gray-200'}`}
                onClick={() => setMode('timesheet')} data-testid="emp-mode-timesheet">
                <div className="font-medium">{t('empInvoiceDlg.fromMyTimesheets')}</div>
                <div className="text-sm text-slate-500">{t('empInvoiceDlg.autoFillFromLoggedHours')}</div>
              </button>
              <button className={`p-4 border-2 rounded-lg text-left transition-colors ${mode === 'manual' ? 'border-blue-600 bg-blue-50' : 'border-gray-200'}`}
                onClick={() => setMode('manual')} data-testid="emp-mode-manual">
                <div className="font-medium">{t('empInvoiceDlg.manualEntry')}</div>
                <div className="text-sm text-slate-500">{t('empInvoiceDlg.customItemsAndAmounts')}</div>
              </button>
            </div>
            {mode === 'timesheet' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>{t('empInvoiceDlg.periodStart')}</Label><Input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} data-testid="emp-period-start" /></div>
                <div><Label>{t('empInvoiceDlg.periodEnd')}</Label><Input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} data-testid="emp-period-end" /></div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel-btn">{t('common.cancel')}</Button>
              {mode === 'timesheet' ? (
                <Button onClick={loadTimesheets} disabled={loadingTs} className="bg-blue-600 hover:bg-blue-700" data-testid="emp-load-ts-btn">
                  {loadingTs ? t('empInvoiceDlg.loading') : t('empInvoiceDlg.loadTimesheets')}
                </Button>
              ) : (
                <Button onClick={() => setStep(2)} className="bg-blue-600 hover:bg-blue-700" data-testid="emp-manual-continue">{t('empInvoiceDlg.continue')}</Button>
              )}
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-5">
            {tsData && (
              <div className="p-3 bg-blue-50 rounded-lg text-sm">
                <strong>{tsData.employee_name}</strong> — {tsData.total_hours} hours across {tsData.projects?.length || 0} project(s)
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div><Label>{t('empInvoiceDlg.issueDate')}</Label><Input type="date" value={form.issue_date} onChange={e => setForm(p => ({...p, issue_date: e.target.value}))} data-testid="emp-issue-date" /></div>
              <div><Label>{t('empInvoiceDlg.saleDate')}</Label><Input type="date" value={form.sale_date} onChange={e => setForm(p => ({...p, sale_date: e.target.value}))} data-testid="emp-sale-date" /></div>
              <div><Label>{t('empInvoiceDlg.dueDate')}</Label><Input type="date" value={form.due_date} onChange={e => setForm(p => ({...p, due_date: e.target.value}))} data-testid="emp-due-date" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label>{t('empInvoiceDlg.currency')}</Label>
                <Select value={form.currency} onValueChange={v => {
                  const bankAcc = {
                    'PLN': form._bank_pln,
                    'EUR': form._bank_eur,
                    'GBP': form._bank_gbp,
                    'USD': form._bank_usd,
                  }[v] || '';
                  setForm(p => ({...p, currency: v, bank_account: bankAcc, vat_rate: p.reverse_charge ? 0 : p.vat_rate}));
                }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['PLN', 'EUR', 'GBP', 'USD'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{t('empInvoiceDlg.vatRate')}</Label><Input type="number" value={form.vat_rate} onChange={e => setForm(p => ({...p, vat_rate: parseFloat(e.target.value) || 0}))} disabled={form.reverse_charge} data-testid="emp-vat-rate" /></div>
              <div>
                <Label>{t('empInvoiceDlg.bankAccount')}</Label>
                <Input value={form.bank_account} onChange={e => setForm(p => ({...p, bank_account: e.target.value}))} placeholder={t('empInvoiceDlg.iban')} data-testid="emp-bank" />
                {(() => {
                  const cached = { PLN: form._bank_pln, EUR: form._bank_eur, GBP: form._bank_gbp, USD: form._bank_usd };
                  const expected = cached[form.currency] || '';
                  if (!expected && Object.values(cached).some(Boolean)) {
                    return <p className="text-xs text-amber-600 mt-1" data-testid="emp-bank-hint">No {form.currency} account on your Employee profile. Add one in Employee → Business.</p>;
                  }
                  if (expected && form.bank_account === expected) {
                    return <p className="text-xs text-emerald-700 mt-1" data-testid="emp-bank-hint">Auto-filled from your Employee profile ({form.currency}).</p>;
                  }
                  if (expected && form.bank_account !== expected) {
                    return <p className="text-xs text-slate-500 mt-1" data-testid="emp-bank-hint">Profile has {expected} for {form.currency} — currently overridden.</p>;
                  }
                  return null;
                })()}
              </div>
            </div>

            <div className="flex items-center gap-3 py-1">
              <input type="checkbox" id="emp-reverse-charge" checked={form.reverse_charge} onChange={e => setForm(p => ({...p, reverse_charge: e.target.checked, vat_rate: e.target.checked ? 0 : 23}))} className="h-4 w-4" data-testid="emp-reverse-charge" />
              <Label htmlFor="emp-reverse-charge" className="cursor-pointer text-sm">{t('empInvoiceDlg.reverseChargeOdwrotneObciEnie')}</Label>
            </div>
            {form.reverse_charge && (
              <div>
                <Label>{t('empInvoiceDlg.reverseChargeText')}</Label>
                <textarea value={form.reverse_charge_text || ''} onChange={e => setForm(p => ({...p, reverse_charge_text: e.target.value}))} className="w-full border rounded-md p-2 text-xs min-h-[60px]" data-testid="emp-reverse-charge-text" />
              </div>
            )}

            <InvoiceHeaderEditor form={form} setForm={setForm} employeeId={editInvoice?.employee_id} />

            <div className="p-3 border rounded-lg space-y-3">
              <h4 className="text-sm font-medium text-slate-600">{t('empInvoiceDlg.sellerDetailsOnInvoice')}</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><Label>{t('empInvoiceDlg.nameBusinessName')}</Label><Input value={form.seller_name} onChange={e => setForm(p => ({...p, seller_name: e.target.value}))} placeholder={t('empInvoiceDlg.businessOrFullName')} data-testid="emp-seller-name" /></div>
                <div><Label>{t('empInvoiceDlg.address')}</Label><Input value={form.seller_address} onChange={e => setForm(p => ({...p, seller_address: e.target.value}))} placeholder={t('empInvoiceDlg.address')} data-testid="emp-seller-address" /></div>
                <div><Label>{t('empInvoiceDlg.nipTaxId')}</Label><Input value={form.seller_tax_id} onChange={e => setForm(p => ({...p, seller_tax_id: e.target.value}))} placeholder={t('empInvoiceDlg.taxId')} data-testid="emp-seller-tax" /></div>
              </div>
            </div>

            {/* Buyer panel — Phase 2: any contractor */}
            <div className="p-3 border rounded-lg space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h4 className="text-sm font-medium text-slate-600">
                  {t('empInvoiceDlg.buyerDetailsOnInvoice')}
                  <span className="ml-2 text-xs font-normal text-slate-400">{t('empInvoiceDlg.leaveBlankToDefaultToExpertfix')}</span>
                </h4>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm"
                    onClick={() => { fetchSavedBuyers(); setShowSavedBuyersPicker(true); }}
                    data-testid="emp-pick-saved-buyer">
                    <FileText className="h-3 w-3 mr-1" /> {t('invoiceDlg.fromSaved')}
                  </Button>
                  <Button variant="outline" size="sm"
                    onClick={handleSaveCurrentBuyer}
                    disabled={!form.buyer_name?.trim()}
                    title={t('empInvoiceDlg.saveTheCurrentBuyerDetailsFor')}
                    data-testid="emp-save-buyer-reuse">
                    <Plus className="h-3 w-3 mr-1" /> {t('empInvoiceDlg.saveBuyer')}
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <Label>{t('empInvoiceDlg.nameCompany')}</Label>
                  <Input value={form.buyer_name}
                    placeholder={t('empInvoiceDlg.expertfixSpZOODefault')}
                    onChange={e => setForm(p => ({...p, buyer_name: e.target.value}))}
                    data-testid="emp-buyer-name" />
                </div>
                <div>
                  <Label>{t('empInvoiceDlg.country')}</Label>
                  <Input value={form.buyer_country_code} maxLength={2}
                    placeholder={t('auto.misc2.pl')}
                    onChange={e => setForm(p => ({...p, buyer_country_code: e.target.value.toUpperCase()}))}
                    data-testid="emp-buyer-country" />
                </div>
              </div>
              <div>
                <Label>{t('empInvoiceDlg.address')}</Label>
                <Input value={form.buyer_address}
                  placeholder={t('empInvoiceDlg.fullAddress')}
                  onChange={e => setForm(p => ({...p, buyer_address: e.target.value}))}
                  data-testid="emp-buyer-address" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>{t('empInvoiceDlg.taxIdNip')}</Label>
                  <Input value={form.buyer_tax_id}
                    placeholder={t('empInvoiceDlg.eG5260250274OrDk61628711')}
                    onChange={e => setForm(p => ({...p, buyer_tax_id: e.target.value}))}
                    data-testid="emp-buyer-tax-id" />
                </div>
                <div>
                  <Label>{t('empInvoiceDlg.euVatOptional')}</Label>
                  <Input value={form.buyer_vat_number}
                    placeholder={t('auto.misc2.dk61628711')}
                    onChange={e => setForm(p => ({...p, buyer_vat_number: e.target.value}))}
                    data-testid="emp-buyer-vat" />
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium text-slate-700">{t('empInvoiceDlg.lineItems')}</h4>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm"
                    onClick={() => { fetchSavedItems(); setShowSavedItemsPicker(true); }}
                    data-testid="emp-pick-saved-item">
                    <FileText className="h-3 w-3 mr-1" /> {t('auto.misc2.fromSaved')}
                  </Button>
                  <Button variant="outline" size="sm" onClick={addItem} data-testid="emp-add-item">
                    <Plus className="h-3 w-3 mr-1" /> {t('invoiceDlg.addItem')}
                  </Button>
                </div>
              </div>
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="w-[40%]">{t('empInvoiceDlg.description')}</TableHead>
                  <TableHead className="w-[10%]">{t('empInvoiceDlg.unit')}</TableHead>
                  <TableHead className="w-[12%] text-right">{t('invoiceDlg.qty')}</TableHead>
                  <TableHead className="w-[15%] text-right">{t('empInvoiceDlg.price')}</TableHead>
                  <TableHead className="w-[15%] text-right">{t('empInvoiceDlg.net')}</TableHead>
                  <TableHead className="w-[8%]"></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {form.line_items.map((it, i) => (
                    <TableRow key={it.id || i}>
                      <TableCell><Input value={it.description} onChange={e => updateItem(i, 'description', e.target.value)} placeholder={t('empInvoiceDlg.description')} /></TableCell>
                      <TableCell>
                        <Select value={it.unit || 'hour'} onValueChange={v => updateItem(i, 'unit', v)}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {['hour', 'service', 'day', 'piece', 'item'].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell><Input type="number" step="0.5" className="text-right h-8" value={it.quantity} onChange={e => updateItem(i, 'quantity', parseFloat(e.target.value) || 0)} /></TableCell>
                      <TableCell><Input type="number" step="0.01" className="text-right h-8" value={it.unit_price} onChange={e => updateItem(i, 'unit_price', parseFloat(e.target.value) || 0)} /></TableCell>
                      <TableCell className="text-right font-medium">{fmtCurrency(it.net_amount, form.currency)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" title={t('empInvoiceDlg.saveItemForReuse')}
                            onClick={() => handleSaveItemForReuse(it)}
                            data-testid="emp-save-item-reuse">
                            <FileText className="h-3 w-3 text-blue-600" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeItem(i)}>
                            <X className="h-3 w-3 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {form.line_items.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-6">{t('empInvoiceDlg.noItemsYet')}</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
              <div className="mt-3 text-right space-y-1">
                <div className="text-sm">{t('empInvoiceDlg.net')} {fmtCurrency(totalNet, form.currency)}</div>
                <div className="text-sm">VAT ({form.vat_rate}%): {fmtCurrency(totalVat, form.currency)}</div>
                <div className="text-lg font-bold text-blue-600" data-testid="emp-invoice-total">{t('empInvoiceDlg.gross')} {fmtCurrency(totalGross, form.currency)}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>{t('empInvoiceDlg.invoiceNumber')}</Label>
                <Input value={form.invoice_number || ''} onChange={e => setForm(p => ({...p, invoice_number: e.target.value}))}
                  placeholder={editInvoice ? editInvoice.invoice_number : t('empInvoiceDlg.autoGeneratedIfEmpty')}
                  data-testid="emp-invoice-number-input" />
                <p className="text-xs text-slate-500 mt-1">{t('empInvoiceDlg.leaveEmptyForAutoNumbering')}</p>
              </div>
              <div>
                <Label>{t('empInvoiceDlg.issuedByNameOnInvoice')}</Label>
                <Input value={form.issued_by_name || ''} onChange={e => setForm(p => ({...p, issued_by_name: e.target.value}))}
                  placeholder={t('empInvoiceDlg.personAuthorisedToIssue')}
                  data-testid="emp-issued-by-name-input" />
              </div>
            </div>

            <div><Label>{t('common.notes')}</Label><RichTextNotes value={form.notes} onChange={val => setForm(p => ({...p, notes: val}))} placeholder={t('empInvoiceDlg.additionalNotes')} /></div>

            <DialogFooter>
              {!editInvoice && <Button variant="outline" onClick={() => setStep(1)} data-testid="dialog-back-btn">{t('common.back')}</Button>}
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel-btn">{t('common.cancel')}</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="emp-save-invoice-btn">
                {saving ? t('empInvoiceDlg.saving') : editInvoice ? t('empInvoiceDlg.update') : t('empInvoiceDlg.createInvoice')}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>

      {/* Saved-items picker (Phase 2) */}
      <Dialog open={showSavedItemsPicker} onOpenChange={setShowSavedItemsPicker}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="emp-saved-items-picker">
          <DialogHeader>
            <DialogTitle>{t('empInvoiceDlg.yourSavedInvoiceItems')}</DialogTitle>
            <DialogDescription>{t('empInvoiceDlg.clickAnItemToInsertIt')}</DialogDescription>
          </DialogHeader>
          {savedItems.length === 0 ? (
            <div className="text-center py-6 text-slate-400">
              {t('empInvoiceDlg.noSavedItemsYetClickThe')}
            </div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>{t('common.name')}</TableHead>
                <TableHead>{t('empInvoiceDlg.description')}</TableHead>
                <TableHead className="text-right">{t('auto.misc2.qty')}</TableHead>
                <TableHead>{t('empInvoiceDlg.unit')}</TableHead>
                <TableHead className="text-right">{t('empInvoiceDlg.price')}</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {savedItems.map(s => (
                  <TableRow key={s.id} className="cursor-pointer hover:bg-slate-50">
                    <TableCell onClick={() => addItemFromSaved(s)} className="font-medium">{s.name}</TableCell>
                    <TableCell onClick={() => addItemFromSaved(s)} className="text-xs">{(s.description || '').slice(0, 80)}</TableCell>
                    <TableCell onClick={() => addItemFromSaved(s)} className="text-right">{s.default_quantity}</TableCell>
                    <TableCell onClick={() => addItemFromSaved(s)}>{s.unit}</TableCell>
                    <TableCell onClick={() => addItemFromSaved(s)} className="text-right">
                      {s.default_unit_price != null ? fmtCurrency(s.default_unit_price, s.default_currency || 'PLN') : '—'}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-7 w-7"
                        onClick={(e) => { e.stopPropagation(); handleDeleteSavedItem(s.id); }}
                        title={t('empInvoiceDlg.deleteSavedItem')}>
                        <Trash2 className="h-3 w-3 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSavedItemsPicker(false)} data-testid="dialog-close-btn">{t('common.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Saved-buyers picker (Phase 3 — feature 3) */}
      <Dialog open={showSavedBuyersPicker} onOpenChange={setShowSavedBuyersPicker}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="emp-saved-buyers-picker">
          <DialogHeader>
            <DialogTitle>{t('empInvoiceDlg.yourSavedBuyers')}</DialogTitle>
            <DialogDescription>{t('empInvoiceDlg.clickARowToApplyThe')}</DialogDescription>
          </DialogHeader>
          {savedBuyers.length === 0 ? (
            <div className="text-center py-6 text-slate-400">
              No saved buyers yet — fill the Buyer panel and click <strong>{t('empInvoiceDlg.saveBuyer')}</strong> to add one.
            </div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>{t('common.name')}</TableHead>
                <TableHead>{t('empInvoiceDlg.taxIdNip')}</TableHead>
                <TableHead>{t('empInvoiceDlg.country')}</TableHead>
                <TableHead>{t('empInvoiceDlg.address')}</TableHead>
                <TableHead>{t('empInvoiceDlg.lastUsed')}</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {savedBuyers.map(b => (
                  <TableRow key={b.id} className="cursor-pointer hover:bg-slate-50">
                    <TableCell onClick={() => applySavedBuyer(b)} className="font-medium">{b.name}</TableCell>
                    <TableCell onClick={() => applySavedBuyer(b)} className="font-mono text-xs">{b.tax_id || '—'}</TableCell>
                    <TableCell onClick={() => applySavedBuyer(b)}>{b.country_code || '—'}</TableCell>
                    <TableCell onClick={() => applySavedBuyer(b)} className="text-xs">{(b.address || '').slice(0, 60)}</TableCell>
                    <TableCell onClick={() => applySavedBuyer(b)} className="text-xs text-slate-500">
                      {b.last_used_at ? <DateText value={b.last_used_at} /> : '—'}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-7 w-7"
                        onClick={(e) => { e.stopPropagation(); handleDeleteSavedBuyer(b.id); }}
                        title={t('empInvoiceDlg.deleteSavedBuyer')}>
                        <Trash2 className="h-3 w-3 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSavedBuyersPicker(false)} data-testid="dialog-close-btn">{t('common.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

// ==================== Payment Dialog ====================
const PaymentDialog = ({ open, onOpenChange, invoice, onSuccess, endpoint }) => {
  const { t } = useTranslation();
  const [data, setData] = useState({ amount: 0, payment_date: format(new Date(), 'yyyy-MM-dd'), payment_method: 'bank_transfer', reference: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && invoice) setData(p => ({ ...p, amount: invoice.amount_remaining || 0 }));
  }, [open, invoice]);

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/${endpoint}/${invoice.id}/payments`, {
        ...data, payment_date: new Date(data.payment_date).toISOString(),
      });
      toast.success(t('empInvoiceDlg.paymentRecorded'));
      onOpenChange(false);
      onSuccess();
    } catch { toast.error(t('empInvoiceDlg.failed')); }
    finally { setSaving(false); }
  };

  if (!invoice) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{t('empInvoiceDlg.recordPayment')}</DialogTitle>
          <DialogDescription>{t('invoices.title')} {invoice.invoice_number} — {t('invoiceDlg.remaining')} {fmtCurrency(invoice.amount_remaining, invoice.currency)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label>{t('invoiceDlg.amount')} ({invoice.currency})</Label><Input type="number" step="0.01" value={data.amount} onChange={e => setData(p => ({...p, amount: parseFloat(e.target.value) || 0}))} /></div>
          <div><Label>{t('empInvoiceDlg.date')}</Label><Input type="date" value={data.payment_date} onChange={e => setData(p => ({...p, payment_date: e.target.value}))} /></div>
          <div><Label>{t('empInvoiceDlg.reference')}</Label><Input value={data.reference} onChange={e => setData(p => ({...p, reference: e.target.value}))} placeholder={t('empInvoiceDlg.transactionId')} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel-btn">{t('common.cancel')}</Button>
          <Button onClick={submit} disabled={saving} data-testid="dialog-submit-btn">{saving ? t('empInvoiceDlg.recording') : t('empInvoiceDlg.recordPayment')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeInvoicesTab;
